import React, { useState, useEffect } from 'react';
import { Star, Send, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    comment: ''
  });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reviews`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
      }
    } catch (error) {
      console.error('Erreur de chargement des avis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Veuillez sélectionner une note');
      return;
    }

    if (!formData.name || !formData.comment) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          rating: rating,
          comment: formData.comment
        })
      });

      if (response.ok) {
        toast.success('Merci pour votre avis ! 🎉');
        setFormData({ name: '', comment: '' });
        setRating(0);
        fetchReviews();
      } else {
        toast.error('Erreur lors de l\'envoi de l\'avis');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (count, interactive = false) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && setRating(star)}
            onMouseEnter={() => interactive && setHoverRating(star)}
            onMouseLeave={() => interactive && setHoverRating(0)}
            className={interactive ? 'cursor-pointer transition-transform hover:scale-110' : ''}
          >
            <Star
              className={`w-6 h-6 ${
                star <= (interactive ? (hoverRating || rating) : count)
                  ? 'fill-yellow-500 text-yellow-500'
                  : 'text-gray-600'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-zinc-950 via-black to-red-950/20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-600 rounded-full mb-6">
              <MessageSquare className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Avis <span className="text-red-600">Clients</span>
            </h1>
            <p className="text-xl text-gray-400">
              Partagez votre expérience avec EM Coaching et aidez d'autres personnes dans leur transformation
            </p>
            
            {reviews.length > 0 && (
              <div className="mt-8 flex items-center justify-center gap-8">
                <div>
                  <div className="text-5xl font-bold text-red-600">{averageRating}</div>
                  <div className="flex gap-1 mt-2">
                    {renderStars(Math.round(averageRating))}
                  </div>
                </div>
                <div className="text-left">
                  <div className="text-2xl font-bold text-white">{reviews.length}</div>
                  <div className="text-gray-400">Avis client{reviews.length > 1 ? 's' : ''}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Review Form */}
      <section className="py-16 bg-zinc-950">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Laisser un Avis</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-zinc-300 mb-2">Votre nom *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Jean Dupont"
                    required
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-red-600"
                  />
                </div>

                {/* Rating */}
                <div>
                  <label className="block text-zinc-300 mb-2">Votre note *</label>
                  {renderStars(rating, true)}
                  {rating > 0 && (
                    <p className="text-sm text-gray-400 mt-2">
                      Vous avez sélectionné {rating} étoile{rating > 1 ? 's' : ''}
                    </p>
                  )}
                </div>

                {/* Comment */}
                <div>
                  <label className="block text-zinc-300 mb-2">Votre commentaire *</label>
                  <textarea
                    value={formData.comment}
                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                    placeholder="Partagez votre expérience avec EM Coaching..."
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-red-600 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6"
                >
                  {submitting ? 'Envoi en cours...' : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Publier mon Avis
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews List */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8">
              Tous les Avis ({reviews.length})
            </h2>

            {loading ? (
              <p className="text-center text-gray-400 py-8">Chargement des avis...</p>
            ) : reviews.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400 text-lg mb-4">Aucun avis pour le moment</p>
                <p className="text-gray-500">Soyez le premier à partager votre expérience !</p>
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((review, index) => (
                  <div
                    key={index}
                    className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center text-white font-bold text-lg">
                          {review.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-white font-semibold">{review.name}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(review.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      {renderStars(review.rating)}
                    </div>
                    <p className="text-gray-300 leading-relaxed">{review.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
